/**
 * Stockage hors ligne de l'appareil (IndexedDB), sans dépendance :
 * - `cache`  : dernières réponses de lecture du serveur (clé = utilisateur + adresse) ;
 * - `outbox` : actions préparées hors ligne, en attente d'envoi ;
 * - `meta`   : session hors ligne (utilisateur, dernière connexion au serveur).
 * Tout échec (navigation privée, stockage plein) est silencieux : l'application marche alors sans hors-ligne.
 */
const DB_NAME = "waitechedu-offline";
const STORES = ["cache", "outbox", "meta"] as const;
export type StoreName = (typeof STORES)[number];

let opening: Promise<IDBDatabase | null> | null = null;

function open(): Promise<IDBDatabase | null> {
  if (opening) return opening;
  opening = new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        for (const name of STORES) if (!request.result.objectStoreNames.contains(name)) request.result.createObjectStore(name);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return opening;
}

function run<T>(store: StoreName, mode: "readonly" | "readwrite", action: (s: IDBObjectStore) => IDBRequest): Promise<T | undefined> {
  return open().then(
    (db) =>
      new Promise<T | undefined>((resolve) => {
        if (!db) return resolve(undefined);
        try {
          const request = action(db.transaction(store, mode).objectStore(store));
          request.onsuccess = () => resolve(request.result as T);
          request.onerror = () => resolve(undefined);
        } catch {
          resolve(undefined);
        }
      }),
  );
}

export const idb = {
  get: <T>(store: StoreName, key: string) => run<T>(store, "readonly", (s) => s.get(key)),
  set: (store: StoreName, key: string, value: unknown) => run(store, "readwrite", (s) => s.put(value, key)).then(() => undefined),
  delete: (store: StoreName, key: string) => run(store, "readwrite", (s) => s.delete(key)).then(() => undefined),
  all: async <T>(store: StoreName): Promise<T[]> => (await run<T[]>(store, "readonly", (s) => s.getAll())) ?? [],
  clear: (store: StoreName) => run(store, "readwrite", (s) => s.clear()).then(() => undefined),
};
